import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { parseFileToCSV, validateData, downloadCSV } from '../../../Utils/fileParser';
import DataPreviewModal from './DataPreviewModal';
import { useDispatch } from 'react-redux';
import { uploadSchwabFiles } from '../../../Redux/Actions/strategyService';

const UploadModal = ({
  open,
  onClose,
  selectedFiles,
  onFileSelect,
  onFileUpload,
  onDownloadTemplate,
  onRemoveFile,
  onError
}) => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingFile, setProcessingFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [processingError, setProcessingError] = useState(null);

  const getSupportedFormats = () => {
    const formats = [
      
      { ext: 'ACC', desc: 'Accounting files', color: 'info' },
      { ext: 'RLC', desc: 'Reconciliation files', color: 'warning' },
      { ext: 'RLD', desc: 'Reconciliation data', color: 'warning' },
      { ext: 'RPS', desc: 'Report files', color: 'secondary' },
      { ext: 'SEC', desc: 'Securities files', color: 'primary' },
      { ext: 'TCF', desc: 'Trade confirmations', color: 'success' },
      { ext: 'TRN', desc: 'Transaction files', color: 'info' },
      { ext: 'ULN', desc: 'Upload loan files', color: 'success' },
      { ext: 'ULT', desc: 'Upload trade files', color: 'default' },
      { ext: 'UPN', desc: 'Upload position files', color: 'default' },
      { ext: 'UPT', desc: 'Upload trade files', color: 'default' },

    ];
    return formats;
  };

  const getFileTypeInfo = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const formats = getSupportedFormats();
    const format = formats.find(f => f.ext.toLowerCase() === extension);
    return format || { ext: extension.toUpperCase(), desc: 'Unknown format', color: 'default' };
  };

  const handlePreviewFile = async (file) => {
    setIsProcessing(true);
    setProcessingFile(file.name);
    setProcessingError(null);
    
    try {
      const result = await parseFileToCSV(file);
      
      if (result.success) {
        // Add validation to the result
        const validation = validateData(result.data);
        const enrichedResult = {
          ...result,
          validation
        };
        
        setParsedData(enrichedResult);
        setShowPreview(true);
      } else {
        setParsedData(result);
        setShowPreview(true);
      }
    } catch (error) {
      const errorData = {
        fileName: file.name,
        error: error.message,
        type: 'error'
      };
      setProcessingError(error.message);
      if (onError) {
        onError(errorData);
      }
    } finally {
      setIsProcessing(false);
      setProcessingFile(null);
    }
  };

  const handleConfirmUpload = (data) => {
    // Call the original upload handler with parsed data
    onFileUpload(data);
    setShowPreview(false);
    setParsedData(null);
  };

  const handleDownloadCSV = (data, fileName) => {
    const csvFileName = fileName.replace(/\.[^/.]+$/, '') + '_converted.csv';
    downloadCSV(data, csvFileName);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setParsedData(null);
  };

  const handleFileInputChange = (event) => {
    setProcessingError(null);
    onFileSelect(event);
  };

  // Validation function to check for required file types
  const validateRequiredFiles = (files) => {
    const requiredTypes = ['TRN'];
    const uploadedTypes = files.map(file => {
      // More robust extension extraction
      const fileName = file.name || '';
      const lastDotIndex = fileName.lastIndexOf('.');
      const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1).toUpperCase() : '';
      return extension;
    });

    const missingFiles = requiredTypes.filter(type => !uploadedTypes.includes(type));
    const presentFiles = requiredTypes.filter(type => uploadedTypes.includes(type));

    return {
      isValid: missingFiles.length === 0,
      missingFiles,
      presentFiles,
      uploadedTypes
    };
  };

  const handleUploadClick = async () => {
    // Validate required files before proceeding
    const validation = validateRequiredFiles(selectedFiles);
    
    if (!validation.isValid) {
      // Show validation error modal
      const errorData = {
        type: 'validation',
        missingFiles: validation.missingFiles,
        presentFiles: validation.presentFiles,
        fileName: selectedFiles.map(f => f.name).join(', '),
        error: `Missing required file types: ${validation.missingFiles.join(', ')}`
      };
      
      if (onError) {
        onError(errorData);
      }
      return;
    }

    // If validation passes, proceed with API upload
    try {
      setIsProcessing(true);
      setProcessingError(null);
      
      // Call the Schwab upload API
      const result = await dispatch(uploadSchwabFiles(selectedFiles));
      
      // Check if the API call was successful
      if (result.payload && (result.payload.status === 201 || result.payload.statusCode === 201)) {
        // Success - show success modal
        const successData = {
          fileName: selectedFiles.map(f => f.name).join(', '),
          originalFormat: 'Schwab Files',
          rowCount: selectedFiles.length,
          headers: ['Files'],
          data: selectedFiles,
          apiResponse: result.payload
        };
        
        // Call the parent's success handler
        if (onFileUpload) {
          onFileUpload(successData);
        }
      } else if (result.error) {
        // Redux thunk error
        const errorData = {
          type: 'error',
          fileName: selectedFiles.map(f => f.name).join(', '),
          error: result.error.message || 'Failed to upload files to server',
          apiResponse: result.error
        };
        
        if (onError) {
          onError(errorData);
        }
      } else {
        // API error
        const errorData = {
          type: 'error',
          fileName: selectedFiles.map(f => f.name).join(', '),
          error: result.payload?.message || 'Failed to upload files to server',
          apiResponse: result.payload
        };
        
        if (onError) {
          onError(errorData);
        }
      }
    } catch (error) {
      // Network or other error
      const errorData = {
        type: 'error',
        fileName: selectedFiles.map(f => f.name).join(', '),
        error: error.message || 'Network error occurred while uploading files'
      };
      
      if (onError) {
        onError(errorData);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  
  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="upload-lots-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '600px',
        height: '700px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px',
          flexShrink: 0
        }}>
          <Box>
            <Typography variant="h4" sx={{
              color: '#141218',
              fontFamily: 'Founders Grotesk, sans-serif',
              fontSize: '24px',
              fontWeight: '600',
              margin: '0 0 4px 0'
            }}>
              Upload Lots
            </Typography>
            <Typography variant="body1" sx={{
              color: '#545454',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              Upload a csv file for lots
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Warning Section */}
        <Box sx={{
          margin: '24px 24px 0 24px',
          padding: '20px',
          backgroundColor: 'rgba(254, 202, 131, 0.20)',
          border: '1px solid #F0E68C',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          flexShrink: 0
        }}>
          <Box sx={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#FFD700',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Typography sx={{
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              lineHeight: 1
            }}>
              !
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{
              color: '#DEA11B',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '15px',
              fontWeight: '700',
              margin: '0 0 12px 0',
              lineHeight: '1.3'
            }}>
              Caution: Upload file in the{' '}
              <span style={{ 
                color: '#1344EB', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: '700'
              }}>
                correct template
              </span>
            </Typography>
            <Typography sx={{
              color: '#000000',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '400',
              margin: '0 0 12px 0',
              lineHeight: '1.4'
            }}>
              Ensure your uploaded file matches the correct template for successful execution.
            </Typography>
            <Typography sx={{
              color: '#1344EB',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '13px',
              fontWeight: '600',
              margin: '0 0 16px 0',
              lineHeight: '1.4'
            }}>
              Required files: .trn, .ult, .uln (all 3 must be uploaded)
            </Typography>
            <Button
              startIcon={<DownloadIcon />}
              onClick={onDownloadTemplate}
              sx={{
                color: '#1344EB',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '14px',
                padding: '0',
                minWidth: 'auto',
                textDecoration: 'underline',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              Download Template
            </Button>
          </Box>
        </Box>

        {/* File Upload Area */}
        <Box sx={{
          margin: '24px 24px 0 24px',
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {selectedFiles.length === 0 ? (
            /* Original Design - When No Files Selected */
            <Box sx={{
              padding: '48px 24px',
              border: '2px dashed #D3D3D3',
              borderRadius: '8px',
              backgroundColor: '#E4E9F980',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1344EB',
                backgroundColor: '#F0F8FF'
              }
            }}
            onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileInputChange}
                accept=".acc,.rlc,.rld,.rps,.sec,.tcf,.trn,.uln,.ult,.upn,.upt"
                style={{ display: 'none' }}
              />
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
              }}>
                <CloudUploadIcon sx={{
                  width: '32px',
                  height: '32px',
                  color: '#1344EB'
                }} />
                <Typography sx={{
                  color: '#1344EB',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '16px',
                  fontWeight: '500',
                  margin: '0'
                }}>
                  Upload Files
                </Typography>
                <Typography sx={{
                  color: '#666666',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  fontWeight: '400',
                  margin: '0',
                  textAlign: 'center'
                }}>
                  Supported:  ACC, RLC, RLD, RPS, SEC, TCF, TRN, ULN, ULT, UPN, UPT up to 10 MB
                </Typography>
                
                {/* Supported Formats Chips */}
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 0.5, 
                  justifyContent: 'center',
                  mt: 1 
                }}>
                  {getSupportedFormats().map((format) => (
                    <Chip
                      key={format.ext}
                      label={format.ext}
                      size="small"
                      color={format.color}
                      variant="outlined"
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            /* Compact Design - When Files Are Selected */
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              height: '100%'
            }}>
              {/* Fixed Header Section */}
              <Box sx={{ 
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px'
              }}>
                {/* Supported Files Description */}
                <Typography sx={{
                  color: '#666666',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '13px',
                  fontWeight: '400',
                  margin: '0',
                  flex: 1
                }}>
                  Supported:  ACC, RLC, RLD, RPS, SEC, TCF, TRN, ULN, ULT, UPN, UPT up to 10 MB
                </Typography>
                
                {/* Add File Button */}
                <Button
                  startIcon={<CloudUploadIcon />}
                  onClick={() => document.getElementById('file-input').click()}
                  sx={{
                    color: '#1344EB',
                    textTransform: 'none',
                    fontWeight: '500',
                    fontSize: '14px',
                    padding: '8px 16px',
                    minWidth: 'auto',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(19, 68, 235, 0.08)'
                    }
                  }}
                >
                  Add File
                </Button>
              </Box>
              
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileInputChange}
                accept=".acc,.rlc,.rld,.rps,.sec,.tcf,.trn,.uln,.ult,.upn,.upt"
                style={{ display: 'none' }}
              />

              {/* Processing Status and Errors - Fixed */}
              <Box sx={{ flexShrink: 0 }}>
                {/* Processing Status */}
                {isProcessing && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">
                        Processing {processingFile}...
                      </Typography>
                    </Box>
                  </Alert>
                )}

                {/* Processing Error */}
                {processingError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Processing Error</AlertTitle>
                    {processingError}
                  </Alert>
                )}
              </Box>

              {/* File Display Area */}
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flex: 1,
                overflow: 'auto',
                maxHeight: '200px',
                paddingRight: '4px'
              }}>
                {selectedFiles.map((file, index) => {
                  const fileInfo = getFileTypeInfo(file.name);
                  const isRequired = ['TRN', 'ULT', 'ULN'].includes(fileInfo.ext);
                  return (
                    <Box key={index} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: isRequired ? '#E8F5E8' : '#F5F5F5',
                      borderRadius: '6px',
                      border: isRequired ? '1px solid #4CAF50' : '1px solid #E0E0E0'
                    }}>
                      {/* File Type Chip */}
                      <Chip
                        label={fileInfo.ext}
                        size="small"
                        color={isRequired ? 'success' : fileInfo.color}
                        sx={{ 
                          fontSize: '10px',
                          height: '24px',
                          fontWeight: 'bold'
                        }}
                      />
                      
                      {/* File Info */}
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                          color: '#333',
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          fontWeight: '400',
                          mb: 0.5
                        }}>
                          {file.name}
                        </Typography>
                        <Typography sx={{
                          color: '#666',
                          fontSize: '12px',
                          fontWeight: '400'
                        }}>
                          {fileInfo.desc} • {(file.size / 1024).toFixed(1)} KB
                          {isRequired && (
                            <span style={{ 
                              color: '#4CAF50', 
                              fontWeight: '600',
                              marginLeft: '8px'
                            }}>
                              • Required
                            </span>
                          )}
                        </Typography>
                      </Box>

                      {/* Preview Button */}
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreviewFile(file)}
                        disabled={isProcessing}
                        sx={{
                          textTransform: 'none',
                          fontSize: '12px',
                          color: '#1344EB',
                          '&:hover': {
                            backgroundColor: 'rgba(19, 68, 235, 0.08)'
                          }
                        }}
                      >
                        Preview
                      </Button>
                      
                      {/* Remove Button */}
                      <IconButton
                        size="small"
                        onClick={() => onRemoveFile(index)}
                        sx={{ 
                          p: 0.5,
                          color: '#666',
                          '&:hover': {
                            backgroundColor: '#FFEBEE',
                            color: '#D32F2F'
                          }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Button */}
        <Box sx={{
          padding: '24px',
          flexShrink: 0
        }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleUploadClick}
            disabled={selectedFiles.length === 0 || isProcessing}
            sx={{
              height: '48px',
              backgroundColor: selectedFiles.length > 0 && !isProcessing ? '#1344EB' : '#E0E0E0',
              color: selectedFiles.length > 0 && !isProcessing ? 'white' : '#666',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '16px',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: selectedFiles.length > 0 && !isProcessing ? '#0D47A1' : '#E0E0E0'
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#666'
              }
            }}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                Uploading...
              </>
            ) : (
              `Upload File${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`
            )}
          </Button>
        </Box>
      </Box>
    </Modal>

    {/* Data Preview Modal */}
    <DataPreviewModal
      open={showPreview}
      onClose={handleClosePreview}
      onBack={() => setShowPreview(false)}
      parsedData={parsedData}
      onConfirmUpload={handleConfirmUpload}
      onDownloadCSV={handleDownloadCSV}
    />
  </>
  );
};

export default UploadModal; 